import type {
  LookbookShareCompany,
  LookbookShareOptions,
  LookbookVisibilityInput,
} from "@/api/lookbook-api";
import { fileTypeLabel } from "@/lib/utils";

/**
 * Pure helpers behind the "what should this lookbook show?" dialog.
 *
 * The shape of the problem: reports mean the same thing for every company, so
 * they are picked once and fan out. Documents do not — file ids belong to one
 * company each — so a bulk selection picks *categories* and lets the server
 * resolve each company's own file, while a single deal picks exact files.
 */

export interface DocumentGroup {
  fileType: string;
  label: string;
  files: LookbookShareCompany["documents"];
}

/** One deal's documents, grouped under their human category, newest first. */
export function groupDocumentsByType(
  documents: LookbookShareCompany["documents"]
): DocumentGroup[] {
  const groups = new Map<string, LookbookShareCompany["documents"]>();

  for (const doc of documents) {
    const existing = groups.get(doc.file_type);
    if (existing) existing.push(doc);
    else groups.set(doc.file_type, [doc]);
  }

  return [...groups.entries()]
    .map(([fileType, files]) => ({
      fileType,
      label: fileTypeLabel(fileType),
      files,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export interface AvailableOption {
  /** file_type for documents, report key for reports. */
  value: string;
  label: string;
  /** How many of the selected deals actually have this. */
  companyCount: number;
}

/**
 * The document categories present across the selection.
 *
 * Only categories some deal actually has are listed. Offering a category no
 * deal has produces a tick that resolves to nothing, which reads as a bug.
 */
export function availableDocumentTypes(
  options: LookbookShareOptions
): AvailableOption[] {
  return options.document_types
    .map((entry) => ({
      value: entry.file_type,
      label: fileTypeLabel(entry.file_type),
      companyCount: entry.company_count,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** The reports that have actually been generated across the selection. */
export function availableReports(
  options: LookbookShareOptions
): AvailableOption[] {
  const byLabel = new Map<string, AvailableOption>();

  // The server lists tearsheets and tearsheets_v2 separately because they are
  // stored differently; to the person picking they are one report.
  for (const report of options.reports) {
    const existing = byLabel.get(report.label);
    if (existing) {
      existing.companyCount = Math.max(
        existing.companyCount,
        report.company_count
      );
    } else {
      byLabel.set(report.label, {
        value: report.key,
        label: report.label,
        companyCount: report.company_count,
      });
    }
  }

  return [...byLabel.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/** Every report key behind the labels the user ticked. */
export function expandReportKeys(
  options: LookbookShareOptions,
  selectedKeys: string[]
): string[] {
  const selectedLabels = new Set(
    options.reports
      .filter((r) => selectedKeys.includes(r.key))
      .map((r) => r.label)
  );
  return options.reports
    .filter((r) => selectedLabels.has(r.label))
    .map((r) => r.key);
}

export interface VisibilityDraft {
  profile: boolean;
  dataRoom: boolean;
  reports: boolean;
  /** file_type categories, used when more than one deal is selected. */
  documentTypes: string[];
  /** exact file ids, used for a single deal. */
  documentIds: string[];
  reportKeys: string[];
}

export const emptyDraft = (): VisibilityDraft => ({
  // Always on, and not the operator's to turn off — the picker renders it
  // locked. A lookbook page with no profile is a bare file list under a company
  // name, which is not a thing anyone means to publish. The field stays on the
  // draft because the wire format still carries it, and because the *public*
  // reader still honours `profile: false` for entries written before this.
  profile: true,
  dataRoom: false,
  reports: false,
  documentTypes: [],
  documentIds: [],
  reportKeys: [],
});

/**
 * The draft an already-configured deal opens with.
 *
 * Only meaningful for a single deal — across a bulk selection the stored
 * configs may disagree, and silently showing one deal's as if it were all of
 * theirs would rewrite the others on save.
 */
export function draftFromCurrent(
  options: LookbookShareOptions
): VisibilityDraft {
  const only =
    options.companies.length === 1 ? options.companies[0].current : null;
  if (!only) return emptyDraft();

  return {
    // Not `only.profile`: an entry stored before the profile was locked on may
    // carry `false`, and re-opening the picker would show a section the UI no
    // longer lets anyone turn back on. Re-saving such an entry turns it on.
    profile: true,
    dataRoom: only.data_room,
    reports: only.reports,
    documentTypes: [],
    documentIds: only.document_ids ?? [],
    reportKeys: only.report_keys ?? [],
  };
}

/** The request body, in whichever document form fits the selection. */
export function buildVisibilityInput(
  draft: VisibilityDraft,
  options: LookbookShareOptions,
  companyCount: number
): LookbookVisibilityInput {
  const base: LookbookVisibilityInput = {
    profile: draft.profile,
    data_room: draft.dataRoom,
    reports: draft.reports,
    report_keys: draft.reports
      ? expandReportKeys(options, draft.reportKeys)
      : [],
  };

  if (!draft.dataRoom) return base;

  return companyCount === 1
    ? { ...base, document_ids: draft.documentIds }
    : { ...base, document_types: draft.documentTypes };
}
